/**
 * The default tab: the last five results as compact rows, with an honest
 * empty state.
 *
 * This tab renders no Season stats block. There is still no team season stats
 * endpoint on the platform, and inventing desktop only content is forbidden.
 * Season stats have a tab of their own now, `TeamStatsTab`, which reads them
 * out of the league's standings row rather than from an endpoint that does not
 * exist, and which appears only when sports-api puts `stats` in the profile's
 * tab list. The next game is NOT inside this tab; it sits above the tab strip.
 *
 * A failed schedule read shows the failed notice, never the empty line
 * (ISSUE-1021).
 */
import type { ReactNode } from "react";

import {
  toTeamGameRowModel,
  type typeTeamScheduleEvent,
} from "./teamGameLogic";
import { TeamScheduleRow } from "./TeamScheduleRow";
import { TEAM_SCHEDULE_FAILED_LINE } from "./teamProfileLogic";
import { TeamReadFailed } from "./TeamReadFailed";
import { recentTeamResults } from "./teamScheduleLogic";

export const TEAM_RECENT_HEADING = "Last five";
export const TEAM_RECENT_EMPTY = "No results yet this season.";

export type typeTeamOverviewTabProps = {
  teamId: string;
  games: readonly typeTeamScheduleEvent[];
  timeZone: string;
  onOpenGame: (eventId: string) => void;
  /** True when the schedule read failed. Wins over the rows and the empty line. */
  isFailed: boolean;
  onRetry: () => void;
};

export const TeamOverviewTab = (props: typeTeamOverviewTabProps): ReactNode => {
  const rows = recentTeamResults(props.games);

  return (
    <section
      data-testid="team-overview-tab"
      className="rounded-md border border-hairline bg-surface"
    >
      <h3 className="px-4 pt-3 pb-1 text-caption text-ink-secondary">
        {TEAM_RECENT_HEADING}
      </h3>
      {props.isFailed ? (
        <TeamReadFailed
          layout="panel"
          testId="team-overview-failed"
          line={TEAM_SCHEDULE_FAILED_LINE}
          onRetry={props.onRetry}
        />
      ) : rows.length === 0 ? (
        <p
          data-testid="team-overview-empty"
          className="px-4 pb-3 text-body text-ink-secondary"
        >
          {TEAM_RECENT_EMPTY}
        </p>
      ) : (
        rows.map((event) => (
          <TeamScheduleRow
            key={event.eventId}
            row={toTeamGameRowModel({
              event,
              teamId: props.teamId,
              timeZone: props.timeZone,
            })}
            onOpenGame={props.onOpenGame}
          />
        ))
      )}
    </section>
  );
};
